import { type HTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

// Badges render both inside light cards AND directly on the dark shell
// (page headers, breadcrumb rows). The previous tinted variants
// (bg-*/10 + colored text) and the transparent `outline` variant dropped
// below WCAG AA on the dark shell — the "Technician" outline pill was
// effectively invisible. Semantic variants are now solid (foreground text on
// a full-strength surface) so they read on any background; `outline` and
// `muted` carry their own opaque surface + dark card-foreground text so they
// stay legible on the shell instead of inheriting an invisible color.
const badgeVariants = cva(
    "inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold leading-normal tracking-wide transition-colors",
    {
        variants: {
            variant: {
                default: "border-transparent bg-primary text-primary-foreground",
                secondary: "border-border bg-secondary text-secondary-foreground",
                destructive: "border-transparent bg-destructive text-destructive-foreground",
                success: "border-transparent bg-success text-success-foreground",
                warning: "border-transparent bg-warning text-warning-foreground",
                outline: "border-border bg-card text-card-foreground",
                muted: "border-border bg-muted text-foreground",
            },
        },
        defaultVariants: { variant: "default" },
    },
);

export interface BadgeProps extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {}

export function Badge({ className, variant, ...props }: BadgeProps) {
    return <div className={cn(badgeVariants({ variant }), className)} {...props} />;
}
